import type { Feature } from "@stb/api-contract"
import { money, toDecimalString } from "@stb/core/money"

import { PRODUCT_CONFIG } from "./product-config.ts"

export type PlanId = keyof typeof PRODUCT_CONFIG.plans

/** What each plan includes, as enforced and sent in `/me` (apps/web plan-limits re-exports it). */
export interface PlanEntitlements {
  features: Feature[]
  monthlyTripQuota: number | null
  aiReadsMonthlyQuota: number | null
  assistantTurnsMonthlyQuota: number | null
  invoicesMonthlyQuota: number | null
  vehicleQuota: number
  teamSeats: number
}

function buildEntitlements(id: PlanId): PlanEntitlements {
  const p = PRODUCT_CONFIG.plans[id]
  return {
    features: [...p.features],
    monthlyTripQuota: p.tripsMonthly,
    aiReadsMonthlyQuota: p.aiReadsMonthly,
    assistantTurnsMonthlyQuota: p.assistantTurnsMonthly,
    invoicesMonthlyQuota: p.invoicesMonthly,
    vehicleQuota: p.vehicles,
    teamSeats: 0,
  }
}

export function planEntitlements(): Record<PlanId, PlanEntitlements> {
  return {
    free: buildEntitlements("free"),
    logbook: buildEntitlements("logbook"),
    pro: buildEntitlements("pro"),
  }
}

/** `999` -> "A$9.99"; whole dollars drop the cents (`9900` -> "A$99"). */
export function formatAud(cents: number): string {
  const decimal = toDecimalString(money(cents, PRODUCT_CONFIG.currency))
  return `A$${decimal.endsWith(".00") ? decimal.slice(0, -3) : decimal}`
}

/** Price of a team plan with `totalSeats` seats per interval; never below the included seats. */
export function teamPlanCents(totalSeats: number, interval: "month" | "year"): number {
  const t = PRODUCT_CONFIG.team
  const extra = Math.max(0, totalSeats - t.includedSeats)
  return interval === "month"
    ? t.baseMonthlyCents + extra * t.extraSeatMonthlyCents
    : t.baseYearlyCents + extra * t.extraSeatYearlyCents
}

/** Free-tier server upload cap per month: the visible quota plus the masked trips (PRD R6.11). */
export function freeUploadCapPerMonth(): number {
  return PRODUCT_CONFIG.plans.free.tripsMonthly + PRODUCT_CONFIG.freeTier.maskedTripsAfterQuota
}

/**
 * Vehicles a team organisation may hold: max(5, ceil(1.5 x Member seats that record trips)), D-TEAMVEH-CAP
 * (Owner 2026-11-01, T881). Integer arithmetic: ceil(3n / 2) without a float.
 */
export function teamVehicleCap(memberSeats: number): number {
  const seats = Math.max(0, Math.trunc(memberSeats))
  return Math.max(5, Math.floor((3 * seats + 1) / 2))
}
